import * as R from 'ramda';
// helpers
import { isTrue, ifElse, isArray, isString, isNotNil, isNotEmpty, isNilOrEmpty, isNotNilAndNotEmpty } from './helpers';
//////////////////////////////////////////////////

const replaceSnake = (str) => str.replace(/_/g, ' ');

const toTitleCase = (string = '') => {
  if (R.is(String, string)) {
    return string.charAt(0).toUpperCase() + string.substr(1).toLowerCase();
  }

  return string;
};

const fromSnakeToTitleCase = (str) => R.compose(
  toTitleCase,
  replaceSnake,
)(str);

const fixTemplateStringSpaces = (string = '') =>
  R.trim(R.replace(/\s{2,}/g, ' ', R.replace(/[\r\n]/g, '', string)));

const stringContains = (string = '', expression) => string.includes(expression);

const isEmptyString = (item) => R.and(isString(item), R.isEmpty(item));

const isNotEmptyString = (item) => R.not(isEmptyString(item));

const fromNullToEmptyString = (item) => ifElse(R.isNil(item), '', item);

const fromEmptyStringToNull = (item) => ifElse(isEmptyString(item), null, item);

const getNumberFromString = (text = '') => text.replace(/\D/g, '');

const getStringDepOnBool = (bool, str) => ifElse(
  isTrue(bool),
  '',
  R.or(str, ', '),
);

const cutString = (text, length, withEllipsis) => {
  if (!text) return text;

  let substring = text;

  if (text.length > length) {
    substring = text.substring(0, length);

    if (withEllipsis) {
      substring = `${substring}...`;
    }
  }

  return substring;
};

const makeURLString = (url = null) => {
  if (R.or(R.isNil(url), R.not(R.is(String, url)))) return url;

  const start = url.slice(0, 4);

  if (R.equals(start, 'http')) {
    return url;
  }

  return `//${url}`;
};

const checkStringsContains = (inputs, checked) => {
  let isAccess = false;

  R.forEach((item) => {
    const contain = R.includes(item, checked);

    if (contain) isAccess = true;
  }, inputs);

  return isAccess;
};

const checkAllPermissions = (allPermissions = [], checked = []) => R.all(
  (permission) => (
    R.is(Array, permission)
      ? checkStringsContains(permission, checked)
      : R.includes(permission, checked)
  ),
  allPermissions,
);

const checkStringsContainsIfInput = (inputs, checked) => {
  if (isNilOrEmpty(inputs)) return true;

  let isAccess = false;

  R.forEach((item) => {
    const contain = R.includes(item, checked);

    if (contain) isAccess = true;
  }, inputs);

  return isAccess;
};

const createStringFromArray = (arr = [], joiner = ' ') => {
  if (R.not(isArray(arr))) return arr;

  const check = (item) => R.and(isNotEmpty(item), isNotNil(item));

  return arr.filter(check).join(joiner);
};

const createStringFromArrayCurried = R.curry((joiner, arr) => {
  if (R.not(isArray(arr))) return arr;

  const check = (item) => R.and(isNotEmpty(item), isNotNil(item));

  return arr.filter(check).join(joiner);
});

const createStringFromArrayWithProp = (
  array = [],
  joiner = ', ',
  prop,
) => {
  const check = (item) => R.and(isNotEmpty(item), isNotNil(item));
  const getProp = (item) => R.path([prop], item);

  if (isNotNilAndNotEmpty(prop)) {
    return array.map(getProp).filter(check).join(joiner);
  }

  return array.filter(check).join(joiner);
};

const getStringTailBySymbol = (str, symbol) => {
  const symbolIndex = R.indexOf(symbol, str);

  return R.slice(R.inc(symbolIndex), Infinity, str);
};

const isPlural = (count, str) =>
  ifElse(
    R.gt(count, 1),
    `${str}s`,
    str,
  );

const getStringWidth = (string, font) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context?.measureText) {
    return 0;
  }

  context.font = font;

  const metrics = context.measureText(string);

  return metrics.width;
};

const isJson = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

const getTextFromHtmlString = (html) => {
  // eslint-disable-next-line no-undef
  const parser = new DOMParser();

  const doc = parser.parseFromString(R.or(html, ''), 'text/html');

  return R.pathOr('', ['body', 'textContent'], doc);
};

export {
  isJson,
  isPlural,
  cutString,
  toTitleCase,
  replaceSnake,
  makeURLString,
  isEmptyString,
  stringContains,
  getStringWidth,
  isNotEmptyString,
  getStringDepOnBool,
  getNumberFromString,
  checkAllPermissions,
  fromSnakeToTitleCase,
  checkStringsContains,
  fromEmptyStringToNull,
  getStringTailBySymbol,
  getTextFromHtmlString,
  createStringFromArray,
  fromNullToEmptyString,
  fixTemplateStringSpaces,
  checkStringsContainsIfInput,
  createStringFromArrayCurried,
  createStringFromArrayWithProp,
};
